<template>
  <section class="contacts-report">
    <div class="content-card">
      <div class="report-heading">
        <div>
          <span>GESTÃO DE CONTACTOS</span>
          <h2>Agentes a contactar</h2>
          <p>Actividade calculada pela última activação (ou criação) de imóvel publicado.</p>
        </div>
        <q-btn round flat icon="refresh" color="primary" :loading="loading" aria-label="Actualizar contactos" @click="fetchContacts" />
      </div>

      <div class="filters-row">
        <q-input
          v-model.trim="search"
          outlined
          dense
          clearable
          debounce="450"
          placeholder="Pesquisar agente por nome, email ou telefone"
          @update:model-value="applyFilters"
        >
          <template #prepend><q-icon name="search" color="primary" /></template>
        </q-input>
        <q-select
          v-model="segment"
          :options="segmentOptions"
          emit-value
          map-options
          outlined
          dense
          label="Actividade"
          @update:model-value="applyFilters"
        />
        <q-select
          v-model="contactedFilter"
          :options="contactOptions"
          emit-value
          map-options
          outlined
          dense
          clearable
          label="Estado do contacto"
          @update:model-value="applyFilters"
        />
      </div>

      <div class="table-summary">
        <div><span>RESULTADOS</span><strong>{{ pagination.total }} {{ pagination.total === 1 ? 'agente encontrado' : 'agentes encontrados' }}</strong></div>
        <div class="table-actions">
          <q-select v-model="sortBy" :options="sortOptions" emit-value map-options borderless dense label="Ordenar por" @update:model-value="changeOrdering" />
          <q-btn round flat color="primary" :icon="sortDirection === 'desc' ? 'south' : 'north'" @click="toggleSortDirection">
            <q-tooltip>{{ sortDirection === 'desc' ? 'Ordem decrescente' : 'Ordem crescente' }}</q-tooltip>
          </q-btn>
        </div>
      </div>

      <div v-if="selected.length || allMatching" class="selection-bar">
        <div><q-icon name="check_circle" /><strong>{{ allMatching ? pagination.total : selected.length }}</strong> agente(s) seleccionado(s)</div>
        <label><q-checkbox v-model="allMatching" color="primary" @update:model-value="toggleAllMatching" /> Seleccionar todos os resultados filtrados</label>
        <div class="selection-actions">
          <q-btn unelevated color="primary" icon="mail" label="Enviar notificação" @click="openMessageDialog" />
          <q-btn flat icon="close" label="Limpar" @click="clearSelection" />
        </div>
      </div>

      <q-table
        v-model:selected="selected"
        :rows="rows"
        :columns="columns"
        row-key="id"
        selection="multiple"
        @update:selected="allMatching = false"
        flat
        hide-bottom
        :loading="loading"
        :rows-per-page-options="[0]"
        class="report-table contacts-table"
      >
        <template #body-cell-agent="props">
          <q-td :props="props">
            <div class="agent-cell">
              <span class="agent-avatar">{{ initials(props.row.agent?.name) }}</span>
              <div>
                <strong>{{ props.row.agent?.name || '—' }}</strong>
                <a v-if="props.row.agent?.email" :href="`mailto:${props.row.agent.email}`">{{ props.row.agent.email }}</a>
                <small>{{ [props.row.agent?.phone, props.row.agent?.whatsapp].filter(Boolean).join(' · ') || 'Sem contacto telefónico' }}</small>
              </div>
            </div>
          </q-td>
        </template>
        <template #body-cell-segment="props">
          <q-td :props="props">
            <q-badge :color="props.row.segment === 'no_activity' ? 'warning' : 'grey-7'" :label="segmentLabel(props.row.segment)" />
          </q-td>
        </template>
        <template #body-cell-property_count="props">
          <q-td :props="props">{{ props.row.active_property_count }}</q-td>
        </template>
        <template #body-cell-total_property_count="props">
          <q-td :props="props">{{ props.row.total_property_count }}</q-td>
        </template>
        <template #body-cell-created_at="props">
          <q-td :props="props">{{ formatDate(props.row.created_at) }}</q-td>
        </template>
        <template #body-cell-last_activity_at="props">
          <q-td :props="props">{{ formatDate(props.row.last_activity_at) }}</q-td>
        </template>
        <template #body-cell-contacted="props">
          <q-td :props="props">
            <q-toggle
              :model-value="props.row.contacted"
              color="positive"
              checked-icon="mark_email_read"
              unchecked-icon="mail_outline"
              :aria-label="props.row.contacted ? 'Marcar como não contactado' : 'Marcar como contactado'"
              @update:model-value="value => markContacted(props.row, value)"
            />
            <small class="contact-status">{{ props.row.contacted ? `Contactado ${formatDate(props.row.contacted_at)}` : 'Por contactar' }}</small>
          </q-td>
        </template>
        <template #no-data>
          <div class="empty-list">
            <q-icon name="manage_search" />
            <strong>Nenhum agente encontrado</strong>
            <span>Altere os filtros para consultar outros contactos.</span>
          </div>
        </template>
      </q-table>

      <footer v-if="pagination.total" class="table-pagination">
        <div class="page-size">
          <span>Mostrar</span>
          <q-select v-model="perPage" :options="perPageOptions" emit-value map-options outlined dense options-dense @update:model-value="changePageSize" />
          <span>por página</span>
        </div>
        <span>Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
        <q-pagination
          v-if="pagination.last_page > 1"
          v-model="page"
          :max="pagination.last_page"
          direction-links
          boundary-links
          @update:model-value="fetchContacts"
        />
      </footer>
    </div>

    <q-dialog v-model="messageDialog" persistent>
      <q-card class="message-dialog">
        <q-card-section class="dialog-heading">
          <div><span>CONTACTAR AGENTES</span><h2>Enviar mensagem</h2><p>Será enviado um email e uma notificação no portal para cada agente.</p></div>
          <q-btn round flat icon="close" v-close-popup />
        </q-card-section>
        <q-card-section class="dialog-body">
          <div class="recipient-summary">
            <q-icon name="group" />
            <div><strong>{{ allMatching ? pagination.total : selected.length }} agente(s)</strong><span>Será enviada uma notificação individual a cada agente.</span></div>
          </div>
          <div class="template-row">
            <q-select v-model="selectedTemplate" :options="templates" option-label="name" outlined dense clearable label="Mensagem predefinida" @update:model-value="applyTemplate" />
            <q-btn outline color="primary" icon="settings" label="Gerir" @click="templateDialog = true" />
          </div>
          <q-input v-model.trim="messageForm.subject" outlined label="Assunto" maxlength="180" counter />
          <div class="message-editor">
            <q-editor v-model="messageForm.message" :toolbar="messageToolbar" min-height="160px" max-height="280px" />
            <div class="editor-counter">{{ messageLength }} / 5000</div>
          </div>
          <q-checkbox v-model="saveAsTemplate" :disable="!!selectedTemplate" :label="selectedTemplate ? 'Mensagem predefinida seleccionada' : 'Guardar esta mensagem como predefinida'" />
          <q-input v-if="saveAsTemplate && !selectedTemplate" v-model.trim="messageForm.template_name" outlined dense label="Nome da mensagem predefinida" maxlength="100" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Cancelar" v-close-popup />
          <q-btn unelevated color="primary" icon="send" label="Enviar" :loading="sending" @click="sendMessage" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="templateDialog">
      <q-card class="template-dialog">
        <q-card-section class="dialog-heading">
          <div><span>BIBLIOTECA PESSOAL</span><h2>Mensagens predefinidas</h2></div>
          <q-btn round flat icon="close" v-close-popup />
        </q-card-section>
        <q-card-section class="template-list">
          <div class="template-actions">
            <q-select v-model="managedTemplate" :options="templates" option-label="name" outlined dense clearable label="Mensagem predefinida" />
            <q-btn round flat icon="edit" :disable="!managedTemplate" aria-label="Editar mensagem predefinida" @click="editTemplate(managedTemplate)" />
            <q-btn round flat color="negative" icon="delete" :disable="!managedTemplate" aria-label="Eliminar mensagem predefinida" @click="deleteTemplate(managedTemplate)" />
          </div>
          <p v-if="!templates.length" class="template-empty">Ainda não criou mensagens predefinidas.</p>
        </q-card-section>
        <q-separator />
        <q-card-section class="template-editor">
          <q-input v-model.trim="templateForm.name" outlined dense label="Nome" maxlength="100" />
          <q-input v-model.trim="templateForm.subject" outlined dense label="Assunto" maxlength="180" />
          <q-editor v-model="templateForm.message" :toolbar="messageToolbar" min-height="130px" max-height="220px" />
          <div class="template-editor-actions">
            <q-btn v-if="templateForm.id" flat label="Cancelar edição" @click="resetTemplateForm" />
            <q-btn unelevated color="primary" :label="templateForm.id ? 'Actualizar' : 'Criar mensagem'" :loading="savingTemplate" @click="saveTemplate" />
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useQuasar } from 'quasar';
import apiClient from '@/config/api';

const $q = useQuasar();
const rows = ref([]);
const selected = ref([]);
const allMatching = ref(false);
const loading = ref(false);
const sending = ref(false);
const savingTemplate = ref(false);
const page = ref(1);
const perPage = ref(10);
const search = ref('');
const segment = ref('all');
const contactedFilter = ref(null);
const sortBy = ref('agent');
const sortDirection = ref('asc');
const pagination = ref({ current_page: 1, last_page: 1, total: 0 });
const messageDialog = ref(false);
const templateDialog = ref(false);
const templates = ref([]);
const selectedTemplate = ref(null);
const managedTemplate = ref(null);
const saveAsTemplate = ref(false);
const messageForm = reactive({ subject: '', message: '', template_name: '' });
const templateForm = reactive({ id: null, name: '', subject: '', message: '' });
const messageToolbar = [
  ['bold', 'italic', 'underline'],
  ['left', 'center', 'right', 'justify'],
  ['unordered', 'ordered'],
  ['undo', 'redo'],
];
const messageLength = computed(() => plainText(messageForm.message).length);

const segmentOptions = [
  { label: 'Todos os agentes sem actividade recente', value: 'all' },
  { label: 'Sem imóveis activos', value: 'no_activity' },
  { label: 'Sem publicação nos últimos 90 dias', value: 'low_activity' },
  { label: 'Com actividade nos últimos 90 dias', value: 'recent_activity' },
];
const contactOptions = [
  { label: 'Contactados', value: 'yes' },
  { label: 'Por contactar', value: 'no' },
];
const perPageOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: 'Todos', value: 'all' },
];
const sortOptions = [
  { label: 'Agente', value: 'agent' },
  { label: 'Imóveis publicados', value: 'property_count' },
  { label: 'Última publicação', value: 'last_activity_at' },
  { label: 'Data de criação da conta', value: 'created_at' },
  { label: 'Estado do contacto', value: 'contacted' },
];
const columns = [
  { name: 'agent', label: 'Agente e contactos', field: row => row.agent?.name, align: 'left' },
  { name: 'segment', label: 'Actividade', field: 'segment', align: 'left' },
  { name: 'property_count', label: 'Imóveis activos', field: 'active_property_count', align: 'center' },
  { name: 'total_property_count', label: 'Total de imóveis', field: 'total_property_count', align: 'center' },
  { name: 'last_activity_at', label: 'Última publicação', field: 'last_activity_at', align: 'left' },
  { name: 'created_at', label: 'Conta criada', field: 'created_at', align: 'left' },
  { name: 'contacted', label: 'Contactado', field: 'contacted', align: 'center' },
];

function queryParams() {
  const params = {
    page: page.value,
    per_page: perPage.value,
    segment: segment.value,
    sort_by: sortBy.value,
    sort_direction: sortDirection.value,
  };
  if (search.value) params.search = search.value;
  if (contactedFilter.value) params.contacted = contactedFilter.value;
  return params;
}

async function fetchContacts() {
  loading.value = true;
  try {
    const { data } = await apiClient.get('/reports/contacts', { params: queryParams() });
    rows.value = data.data.data || [];
    pagination.value = data.data;
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível carregar os contactos.' });
  } finally {
    loading.value = false;
  }
}

function applyFilters() {
  page.value = 1;
  clearSelection();
  fetchContacts();
}

function changeOrdering() {
  page.value = 1;
  fetchContacts();
}

function toggleSortDirection() {
  sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc';
  changeOrdering();
}

function changePageSize() {
  page.value = 1;
  clearSelection();
  fetchContacts();
}

function clearSelection() {
  selected.value = [];
  allMatching.value = false;
}

function toggleAllMatching(value) {
  if (value) selected.value = [];
}

function plainText(value) {
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
}

function initials(name) {
  return String(name || 'GK').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat('pt-MZ', { dateStyle: 'short' }).format(new Date(value))
    : '—';
}

function segmentLabel(value) {
  return ({
    no_activity: 'Sem imóveis activos',
    low_activity: 'Sem actividade há 90+ dias',
    recent_activity: 'Actividade recente',
  }[value] || value);
}

async function markContacted(row, contacted) {
  try {
    await apiClient.patch('/reports/listings/contacted', {
      catalog: 'agent',
      listing_ids: [row.id],
      contacted,
    });
    row.contacted = contacted;
    row.contacted_at = contacted ? new Date().toISOString() : null;
    $q.notify({ type: 'positive', message: 'Estado de contacto actualizado.' });
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível actualizar o estado de contacto.' });
  }
}

function openMessageDialog() {
  messageDialog.value = true;
  loadTemplates();
}

async function loadTemplates() {
  try {
    const { data } = await apiClient.get('/reports/message-templates');
    templates.value = data.data || [];
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível carregar as mensagens predefinidas.' });
  }
}

function applyTemplate(template) {
  if (!template) {
    saveAsTemplate.value = false;
    return;
  }
  messageForm.subject = template.subject;
  messageForm.message = template.message;
  saveAsTemplate.value = true;
}

async function sendMessage() {
  if (!messageForm.subject || !plainText(messageForm.message)) {
    return $q.notify({ type: 'warning', message: 'Preencha o assunto e a mensagem.' });
  }
  if (messageLength.value > 5000) {
    return $q.notify({ type: 'warning', message: 'A mensagem não pode exceder 5000 caracteres.' });
  }
  if (!allMatching.value && !selected.value.length) {
    return $q.notify({ type: 'warning', message: 'Seleccione pelo menos um agente.' });
  }

  sending.value = true;
  try {
    if (saveAsTemplate.value && !selectedTemplate.value && messageForm.template_name) {
      await apiClient.post('/reports/message-templates', {
        name: messageForm.template_name,
        subject: messageForm.subject,
        message: messageForm.message,
      });
      await loadTemplates();
    }
    const payload = {
      catalog: 'agent',
      listing_ids: selected.value.map(row => row.id),
      all_matching: allMatching.value,
      template_id: selectedTemplate.value?.id || null,
      subject: messageForm.subject,
      message: messageForm.message,
    };
    const { data } = await apiClient.post('/reports/listings/notify', payload, { params: queryParams() });
    $q.notify({ type: 'positive', message: data.message });
    messageDialog.value = false;
    Object.assign(messageForm, { subject: '', message: '', template_name: '' });
    selectedTemplate.value = null;
    saveAsTemplate.value = false;
    clearSelection();
    fetchContacts();
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível enviar a notificação.' });
  } finally {
    sending.value = false;
  }
}

function editTemplate(template) {
  if (!template) return;
  managedTemplate.value = template;
  Object.assign(templateForm, {
    id: template.id,
    name: template.name,
    subject: template.subject,
    message: template.message,
  });
}

function resetTemplateForm() {
  Object.assign(templateForm, { id: null, name: '', subject: '', message: '' });
}

async function saveTemplate() {
  if (!templateForm.name || !templateForm.subject || !plainText(templateForm.message)) {
    return $q.notify({ type: 'warning', message: 'Preencha todos os campos da mensagem.' });
  }
  if (plainText(templateForm.message).length > 5000) {
    return $q.notify({ type: 'warning', message: 'A mensagem não pode exceder 5000 caracteres.' });
  }

  savingTemplate.value = true;
  try {
    const payload = { name: templateForm.name, subject: templateForm.subject, message: templateForm.message };
    if (templateForm.id) {
      await apiClient.put(`/reports/message-templates/${templateForm.id}`, payload);
    } else {
      await apiClient.post('/reports/message-templates', payload);
    }
    await loadTemplates();
    resetTemplateForm();
    managedTemplate.value = null;
    $q.notify({ type: 'positive', message: 'Mensagem predefinida guardada.' });
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível guardar a mensagem.' });
  } finally {
    savingTemplate.value = false;
  }
}

async function deleteTemplate(template) {
  if (!template) return;
  try {
    await apiClient.delete(`/reports/message-templates/${template.id}`);
    await loadTemplates();
    if (selectedTemplate.value?.id === template.id) {
      selectedTemplate.value = null;
      saveAsTemplate.value = false;
    }
    if (managedTemplate.value?.id === template.id) managedTemplate.value = null;
    $q.notify({ type: 'positive', message: 'Mensagem removida.' });
  } catch (error) {
    $q.notify({ type: 'negative', message: error.response?.data?.message || 'Não foi possível remover a mensagem.' });
  }
}

onMounted(fetchContacts);
</script>

<style scoped>
.contacts-report { display: grid; gap: 18px; }
.content-card { overflow: hidden; background: #fff; border: 1px solid #e5eaf1; border-radius: 16px; box-shadow: 0 6px 22px rgba(15,31,49,.04); }
.report-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; padding: 22px 22px 16px; }
.report-heading span, .table-summary span { color: #b18312; font-size: 10px; font-weight: 800; letter-spacing: .14em; }
.report-heading h2 { margin: 4px 0; color: #0d1b2a; font-size: 21px; }
.report-heading p { margin: 0; color: #7b8798; font-size: 12px; }
.filters-row { display: grid; grid-template-columns: minmax(280px, 1fr) 250px 220px; gap: 10px; align-items: center; padding: 4px 22px 20px; }
.table-summary { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 22px; border-top: 1px solid #eef1f5; background: #fbfcfe; }
.table-summary>div:first-child { display: flex; flex-direction: column; gap: 3px; }
.table-summary strong { color: #0d1b2a; font-size: 13px; }
.table-actions { display: flex; align-items: center; gap: 8px; }
.table-actions .q-select { min-width: 190px; }
.selection-bar { position: sticky; top: 8px; z-index: 4; display: flex; align-items: center; gap: 18px; padding: 12px 16px; border: 1px solid #e7c86e; border-radius: 12px; background: #fff8dc; box-shadow: 0 7px 24px rgba(13,27,42,.1); }
.selection-bar>div:first-child { display: flex; align-items: center; gap: 7px; color: #654900; }
.selection-bar label { display: flex; align-items: center; color: #654900; font-size: 12px; }
.selection-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.agent-cell { display: flex; align-items: center; gap: 9px; min-width: 230px; }
.agent-avatar { display: grid; flex: 0 0 34px; width: 34px; height: 34px; place-items: center; border-radius: 50%; background: #fff0bd; color: #172235; font-size: 10px; font-weight: 800; }
.agent-cell div { display: flex; flex-direction: column; gap: 2px; }
.agent-cell strong { color: #0d1b2a; }
.agent-cell a { color: #8a6712; font-size: 10px; text-decoration: none; }
.agent-cell small, .contact-status { color: #7b8798; font-size: 10px; }
.table-pagination { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 22px; border-top: 1px solid #eef1f5; color: #64748b; font-size: 12px; }
.page-size { display: flex; align-items: center; gap: 8px; }
.page-size .q-select { width: 84px; }
.message-dialog { width: min(680px, 94vw); max-width: 94vw; border-radius: 15px; }
.template-dialog { width: min(760px, 94vw); max-height: 90vh; border-radius: 15px; }
.dialog-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 22px 24px 14px; }
.dialog-heading span { color: #b18312; font-size: 10px; font-weight: 800; letter-spacing: .14em; }
.dialog-heading h2 { margin: 4px 0; color: #0d1b2a; font-size: 21px; }
.dialog-heading p { margin: 0; color: #7b8798; font-size: 12px; }
.dialog-body { display: grid; gap: 14px; padding: 10px 24px 18px; }
.recipient-summary { display: flex; align-items: center; gap: 12px; padding: 13px; border-radius: 10px; background: #f7f3e8; }
.recipient-summary>.q-icon { font-size: 26px; color: #a5790d; }
.recipient-summary div { display: grid; }
.recipient-summary span { color: #7b8798; font-size: 11px; }
.template-row { display: grid; grid-template-columns: 1fr auto; gap: 10px; }
.template-list { display: grid; gap: 9px; }
.template-actions { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 6px; }
.template-empty { text-align: center; white-space: normal; }
.template-editor { display: grid; gap: 10px; }
.template-editor-actions { display: flex; justify-content: flex-end; gap: 8px; }
.message-editor { display: grid; gap: 6px; }
.message-editor :deep(.q-editor) { border: 1px solid #c2c2c2; border-radius: 4px; }
.editor-counter { color: #7b8798; font-size: 10px; text-align: right; }
.empty-list { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 36px; color: #7b8798; }
.empty-list .q-icon { font-size: 30px; }
.empty-list strong { color: #0d1b2a; }
@media (max-width: 760px) {
  .filters-row { grid-template-columns: 1fr; }
  .table-pagination { align-items: flex-start; flex-direction: column; }
  .table-summary, .selection-bar { align-items: flex-start; flex-wrap: wrap; }
  .selection-actions { width: 100%; margin-left: 0; }
  .table-actions { width: 100%; }
  .table-actions .q-select { flex: 1; }
  .template-row { grid-template-columns: 1fr; }
  .table-pagination { display: grid; grid-template-columns: 1fr; justify-items: start; }
}
</style>
